// ═══════════════════════════════════════════════════════════════
// PAGE PILIER « SECRÉTARIAT SOCIAL » — CONTENU BILINGUE PARTAGÉ
//
// Pourquoi ce fichier existe (SEO, 24/08/2026)
// ────────────────────────────────────────────
// Trois sites de comparaison purs (mon-secretariat-social.be,
// secretariat-social-belgique.be/.com) nous devancent sur la requête
// générique « choisir un secrétariat social ». Mesuré le 24/08 : leurs pages
// font 23 000 à 27 000 caractères et répondent à NEUF questions sur UNE seule
// page. Nous répondions aux mêmes neuf questions — mais éclatées sur
// 19 articles de 4 000 à 20 000 caractères. Google voyait 19 pages moyennes
// face à une page complète. Le défaut n'était pas le contenu, c'était sa
// FRAGMENTATION.
//
// Cette page rassemble le jeu de questions complet et renvoie vers les
// 19 articles, qui deviennent son cluster. Elle ne les remplace pas.
//
// ⛔ CE QU'ON NE COPIE PAS À LA CONCURRENCE
// Ces trois sites sont des annuaires d'affiliation : ils gagnent au renvoi,
// donc lister 40 concurrents leur rapporte. Aureus est VENDEUR — publier
// l'annuaire enverrait notre trafic chez nos concurrents. On répond aux
// questions, on ne tient pas la billetterie.
//
// 🛡️ R1 / R2 : aucun chiffre légal ni tarifaire en dur ici.
//   · les prix viennent de catalogue-services.js (getPrixService)
//   · chaque affirmation juridique porte sa source et sa date de lecture
//   · le cran de confiance suit la convention R2 bis (✅ / 🟠 / 🔴)
//
// ⚖️ POSITIONNEMENT LÉGAL — non négociable (cf. CLAUDE.md)
// Aureus IA SRL est PRESTATAIRE DE SERVICES au sens ONSS, pas secrétariat
// social agréé. La dénomination « secrétariat social » est réservée aux
// agréés : cette page l'emploie pour parler du MÉTIER et des AGRÉÉS, jamais
// pour se désigner.
// ═══════════════════════════════════════════════════════════════

import { getPrixService } from '@/app/lib/catalogue-services';

// ── Sources primaires, lues le 24/08/2026 ─────────────────────────────────
// Instructions administratives ONSS, version 2026/2 — fetch nu, UA Chrome,
// HTTP 200. C'est la version courante au jour de la lecture.
export const SOURCES = {
  onssTypes: {
    url: 'https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/employers/mandataries/socialserviceproviders.html',
    titre: 'ONSS — Secrétariats sociaux et prestataires de services',
    lu: '2026-08-24', cran: '✅',
  },
  onssMission: {
    url: 'https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/employers/mandataries/description_mission.html',
    titre: 'ONSS — Description de la mission des mandataires',
    lu: '2026-08-24', cran: '✅',
  },
  onssMandat: {
    url: 'https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/employers/mandataries/mandate.html',
    titre: 'ONSS — Le mandat',
    lu: '2026-08-24', cran: '✅',
  },
  listeMB: {
    url: 'https://www.ejustice.just.fgov.be/cgi/article_body.pl?language=fr&caller=summary&pub_date=21-12-15&numac=2021205711',
    titre: 'Moniteur belge — Liste des secrétariats sociaux agréés (NUMAC 2021205711)',
    lu: '2026-08-24', cran: '✅',
  },
};

// ⚠️ LE COMPTE ET SA DATE VONT ENSEMBLE — ne jamais servir l'un sans l'autre.
// Relevé le 24/08/2026 sur le texte officiel : 40 entités mères dans la liste
// « à la date du 05/11/2021 » (publiée au Moniteur le 15/12/2021), dont une
// (ABANTE, n° 990) que la liste elle-même signale devenue succursale au
// 01/01/2021. Plusieurs appartiennent au même groupe (deux agréments PARTENA,
// deux ACERTA) : le nombre d'ENSEIGNES est donc plus bas que le nombre
// d'agréments.
//
// 🟠 RÉSERVE DE MÉTHODE, à dire telle quelle : l'ONSS écrit que le Moniteur
// publie cette liste CHAQUE ANNÉE. Celle-ci a près de cinq ans et une plus
// récente n'a pas été localisée le 24/08. On publie donc le compte AVEC sa
// date, jamais comme un état 2026. « 23 agréés », qui traînait sur deux de nos
// articles sans source, était faux dans les deux dimensions : le nombre et
// l'autorité qui agrée.
export const LISTE_AGREES = {
  nombre: 40,
  dateListe: '05/11/2021',
  datePublication: '15/12/2021',
  source: SOURCES.listeMB,
};

// ── Le cluster : les articles que cette page rassemble ────────────────────
// Slugs vérifiés contre blog-registry.js par
// __tests__/pilier-secretariat-social.test.js — un slug mort casse le test.
export const CLUSTER = [
  { slug: 'secretariat-social-belgique', fr: 'Secrétariat social : tout comprendre', nl: 'Sociaal secretariaat: alles begrijpen' },
  { slug: 'comparatif-secretariats-sociaux-2026', fr: 'Comparatif des secrétariats sociaux', nl: 'Vergelijking van de sociale secretariaten' },
  { slug: 'prix-secretariat-social-belgique', fr: 'Prix et frais cachés', nl: 'Prijzen en verborgen kosten' },
  { slug: 'changer-secretariat-social-belgique', fr: 'Changer de secrétariat social', nl: 'Van sociaal secretariaat veranderen' },
  { slug: 'meilleur-secretariat-social-belgique', fr: 'Lequel choisir ?', nl: 'Welk secretariaat kiezen?' },
  { slug: 'alternative-secretariat-social-pme', fr: 'L’alternative pour une PME', nl: 'Het alternatief voor een kmo' },
  { slug: 'alternative-secretariat-social-numerique', fr: 'Le passage au numérique', nl: 'De overstap naar digitaal' },
  { slug: 'secretariat-social-digital-belgique', fr: 'Ce qu’est un acteur digital', nl: 'Wat een digitale speler is' },
  { slug: 'secretariat-social-fiduciaire', fr: 'Pour une fiduciaire', nl: 'Voor een boekhoudkantoor' },
  { slug: 'logiciel-paie-belgique', fr: 'Quel logiciel de paie ?', nl: 'Welke loonsoftware?' },
  { slug: 'logiciel-paie-gratuit-belgique', fr: 'Un logiciel gratuit existe-t-il ?', nl: 'Bestaat er gratis software?' },
  { slug: 'premier-employe-belgique', fr: 'Votre premier employé', nl: 'Uw eerste werknemer' },
  { slug: 'secretariat-social-horeca', fr: 'Horeca — CP 302', nl: 'Horeca — PC 302' },
  { slug: 'secretariat-social-construction', fr: 'Construction — CP 124', nl: 'Bouw — PC 124' },
  { slug: 'secretariat-social-transport', fr: 'Transport — CP 140', nl: 'Transport — PC 140' },
  { slug: 'beste-sociaal-secretariaat-belgie', fr: 'Beste sociaal secretariaat (NL)', nl: 'Beste sociaal secretariaat' },
  { slug: 'digitaal-sociaal-secretariaat-belgie', fr: 'Digitaal sociaal secretariaat (NL)', nl: 'Digitaal sociaal secretariaat' },
];

// Les prix ne sont JAMAIS écrits ici : ils sont lus au rendu (GATE B).
export const prixFiche = () => getPrixService('paie');
export const prixAffiliation = () => getPrixService('onboarding');
export const prixReglement = () => getPrixService('reglement_travail');
